import React, { useEffect, useState } from 'react';
import { fetchMyProductsForInvoice, issueChatInvoice } from '../lib/orderInvoice';
import { amIExclusiveSeller } from '../lib/marketExclusives';
import { sb } from '../supabase';

// Supplier composes a chat invoice. Two modes:
//  • standalone (header "🧾 Invoice"): pick a product + qty + agreed unit price.
//  • from a purchase request: the product is locked, the buyer's variants are pre-
//    filled (supplier enters a USD unit price per variant), and the request's
//    details flow into the invoice's specs panel. On issue the parent sends an
//    [invoice:ID] message and the request flips to "quoted".
// A «حصريات معبر» seller (owner, 2026-10-29) invoices with no Maabar fee and
// adds the negotiated shipping as its own line (kind 'shipping'). They type the
// amounts in USD or SAR, whichever they agreed in (owner, 2026-10-31); the
// trader always sees and pays SAR — a USD invoice's rate is locked at issue and
// read through payment_quote(). issue_chat_invoice enforces the same.
const T = {
  ar: {
    title: 'إصدار فاتورة', product: 'المنتج', qty: 'الكمية', price: 'سعر الوحدة المتفق (USD)',
    variant: 'المواصفة', unit: 'سعر الوحدة', currency: 'العملة',
    material: 'المادة', lead: 'مدة التجهيز', incoterms: 'Incoterms', port: 'ميناء الشحن', notes: 'ملاحظات',
    fee: 'رسوم مَعبر (5%)', total: 'الإجمالي', issue: 'إصدار وإرسال', cancel: 'إلغاء',
    none: 'لا توجد منتجات نشطة.', issuing: 'جارٍ الإصدار…', priceNote: 'أسعارك بالدولار — يشوفها التاجر بالريال تلقائياً.',
    exPrice: (c) => `سعر الوحدة المتفق (${c})`, exShip: (c) => `الشحن المتفق (${c})`, shipLine: 'الشحن · Shipping', subtotal: 'المنتجات',
    exNote: 'حصريات معبر: المبالغ بالريال، وهذا ما يدفعه التاجر بالضبط — بدون رسوم.',
    exNoteUsd: 'حصريات معبر: اكتب المبالغ بالدولار — التاجر يشوفها ويدفعها بالريال، بدون رسوم. السعر يُثبَّت عند الإصدار.',
    exCur: 'عملة المبالغ', traderPays: 'يدفع التاجر تقريباً',
  },
  en: {
    title: 'Issue invoice', product: 'Product', qty: 'Quantity', price: 'Unit price, agreed (USD)',
    variant: 'Spec', unit: 'Unit price', currency: 'Currency',
    material: 'Material', lead: 'Lead time', incoterms: 'Incoterms', port: 'Port of loading', notes: 'Notes',
    fee: 'Maabar fee (5%)', total: 'Total', issue: 'Issue & send', cancel: 'Cancel',
    none: 'No active products.', issuing: 'Issuing…', priceNote: 'Your prices are in USD — the buyer sees SAR automatically.',
    exPrice: (c) => `Unit price, agreed (${c})`, exShip: (c) => `Shipping, agreed (${c})`, shipLine: 'الشحن · Shipping', subtotal: 'Products',
    exNote: 'MAABAR Exclusives: amounts are in SAR and are exactly what the trader pays — no fee.',
    exNoteUsd: 'MAABAR Exclusives: type amounts in USD — the trader sees and pays SAR, no fee. The rate is fixed when you issue.',
    exCur: 'Currency of amounts', traderPays: 'Trader pays about',
  },
  zh: {
    title: '开具发票', product: '产品', qty: '数量', price: '约定单价 (USD)',
    variant: '规格', unit: '单价', currency: '货币',
    material: '材质', lead: '交货期', incoterms: 'Incoterms', port: '装货港', notes: '备注',
    fee: 'Maabar 服务费 (5%)', total: '合计', issue: '开具并发送', cancel: '取消',
    none: '没有上架的产品。', issuing: '正在开具…', priceNote: '您的价格以美元计 — 买家自动看到沙特里亚尔。',
    exPrice: (c) => `约定单价 (${c})`, exShip: (c) => `约定运费 (${c})`, shipLine: 'الشحن · Shipping', subtotal: '产品',
    exNote: 'MAABAR 独家：金额以沙特里亚尔计，即买家实际支付的金额 — 无服务费。',
    exNoteUsd: 'MAABAR 独家：请以美元填写金额 — 买家看到并以沙特里亚尔支付，无服务费。汇率在开具时锁定。',
    exCur: '金额货币', traderPays: '买家约支付',
  },
};
const money = (n, c) => `${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c || 'USD'}`;

export default function ChatInvoiceComposer({ buyerId, lang = 'ar', onIssued, onClose, request = null }) {
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const fromReq = !!request;
  const [products, setProducts] = useState([]);
  const [productRef, setProductRef] = useState(request?.product_ref || '');
  const [qty, setQty] = useState(String(request?.quantity || '1'));
  const [price, setPrice] = useState('');
  // variant rows (from-request mode): [{label, qty, unit_price}]
  const [rows, setRows] = useState(
    fromReq
      ? (Array.isArray(request.variants) && request.variants.length
          ? request.variants.map((v) => ({ label: v.label || '', qty: v.qty || 0, unit_price: '' }))
          : [{ label: '', qty: Number(request.quantity) || 1, unit_price: '' }])
      : [],
  );
  const [material, setMaterial] = useState('');
  const [lead, setLead] = useState('');
  const [incoterms, setIncoterms] = useState(request?.details?.incoterms || '');
  const [port, setPort] = useState('');
  const [notes, setNotes] = useState(request?.details?.notes || '');
  const [busy, setBusy] = useState(false);
  const [excl, setExcl] = useState(false);
  const [ship, setShip] = useState('');
  const [exCur, setExCur] = useState('USD');   // an exclusive seller's currency: USD or SAR
  const [sarRate, setSarRate] = useState(null); // SAR per USD now, for the preview only
  useEffect(() => { let alive = true; amIExclusiveSeller().then((v) => { if (alive) setExcl(v); }); return () => { alive = false; }; }, []);
  useEffect(() => {
    if (!excl) return undefined;
    let alive = true;
    sb.rpc('current_sar_rate', { p_currency: 'USD' }).then(({ data, error }) => {
      if (error) { console.error('[ChatInvoiceComposer] current_sar_rate failed:', error.code || '', error.message); return; }
      if (alive && Number(data) > 0) setSarRate(Number(data));
    });
    return () => { alive = false; };
  }, [excl]);

  useEffect(() => {
    (async () => {
      const list = await fetchMyProductsForInvoice();
      setProducts(list);
      if (!fromReq && list[0]) setProductRef(list[0].id);
    })();
  }, [fromReq]);

  const prod = products.find((p) => p.id === productRef);
  const pname = (isAr ? prod?.name_ar : prod?.name_en) || prod?.name_ar || prod?.name_en || '';

  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const subtotal = fromReq
    ? rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0)
    : (Number(qty) || 0) * (Number(price) || 0);
  const shipping = excl ? Math.max(0, Number(ship) || 0) : 0;
  const fee = excl ? 0 : Math.round(subtotal * 5) / 100;
  const total = subtotal + shipping + fee;
  // USD only (owner, 2026-10-06), in both modes; issue_chat_invoice refuses
  // anything else. The standalone mode used to take the product's currency.
  // An exclusive seller: USD or SAR, as they choose; stored as chosen, never relabelled.
  const cur = excl ? exCur : 'USD';
  // What the trader will pay, for an exclusive USD invoice: the server locks the
  // rate in force at issue (current_sar_rate) and payment_quote() rounds the
  // same way. A preview — the trader's card is the figure that counts.
  const curLbl = isAr && cur === 'SAR' ? 'ر.س' : cur;
  const traderSar = excl && cur === 'USD' && sarRate ? Math.round(total * sarRate * 100) / 100 : null;
  const shipItem = shipping > 0 ? [{ desc: t.shipLine, kind: 'shipping', qty: 1, unit_price: shipping, amount: shipping }] : [];

  const buildSpecs = () => {
    const d = request?.details || {};
    return [
      rowdef('المادة · Material', material),
      rowdef('التخصيص · Customization', d.customization),
      rowdef('التغليف · Packaging', d.packaging),
      rowdef('مدة التجهيز · Lead time', lead),
      rowdef('الوجهة · Destination', d.destination),
      rowdef('الموعد · Needed by', d.deadline),
      // The trader reads these specs on their invoice, and sees SAR only
      // (owner, 2026-09-29): a target is carried over only when it is in SAR
      // (every target typed since then is). A legacy USD target stays on the
      // purchase-request card, where each side reads it in its own currency.
      d.target_price && String(d.target_currency || '').toUpperCase() === 'SAR'
        ? { k: 'السعر المستهدف · Target', v: `${d.target_price} SAR` } : null,
      d.notes ? { k: 'ملاحظات · Notes', v: String(d.notes), wide: true } : null,
    ].filter(Boolean);
  };
  const rowdef = (k, v) => (v && String(v).trim() ? { k, v: String(v).trim() } : null);

  const doIssue = async () => {
    if (!productRef || !buyerId) return;
    setBusy(true);
    try {
      let payload = { buyerId, productRef, currency: cur, incoterms: incoterms || null, port: port || null, notes: notes || null };
      if (fromReq) {
        const lineItems = rows
          .filter((r) => (Number(r.qty) || 0) > 0)
          .map((r) => ({
            desc: pname || (isAr ? 'منتج' : 'Product'),
            variant: r.label || undefined,
            qty: Number(r.qty) || 0,
            unit_price: Number(r.unit_price) || 0,
            amount: (Number(r.qty) || 0) * (Number(r.unit_price) || 0),
            attrs: r.label ? [{ k: t.variant, v: r.label }] : undefined,
          }));
        payload = { ...payload, lineItems: [...lineItems, ...shipItem], specs: buildSpecs(), requestId: request.id };
      } else if (excl) {
        const q = Number(qty) || 0, u = Number(price) || 0;
        payload = { ...payload, quantity: qty, unitPrice: price, lineItems: [{ desc: pname || (isAr ? 'منتج' : 'Product'), qty: q, unit_price: u, amount: q * u }, ...shipItem] };
      } else {
        payload = { ...payload, quantity: qty, unitPrice: price };
      }
      const inv = await issueChatInvoice(payload);
      onIssued?.(inv);
    } catch (e) { alert(e.message || 'Error'); setBusy(false); }
  };

  const input = { width: '100%', padding: '9px 11px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box', marginTop: 5 };
  const lbl = { fontSize: 12, color: 'var(--text-secondary)' };
  const curPicker = (
    <div role="radiogroup" aria-label={t.exCur} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '0 0 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
      <span>{t.exCur}</span>
      {['USD', 'SAR'].map((c) => (
        <button key={c} type="button" role="radio" aria-checked={cur === c} data-invoice-cur={c} onClick={() => setExCur(c)}
          style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${cur === c ? '#141210' : 'var(--border)'}`, background: cur === c ? '#141210' : 'none', color: cur === c ? '#F7F4EE' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{c}</button>
      ))}
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} dir={isAr ? 'rtl' : 'ltr'} style={{ background: 'var(--bg-overlay)', borderRadius: 14, maxWidth: 480, width: '100%', overflow: 'hidden' }}>
        <div style={{ background: '#0E0D0C', color: '#F3EFE7', padding: '15px 22px', fontSize: 16, fontWeight: 600, borderBottom: '2px solid #9A7B4F' }}>{t.title}</div>
        <div style={{ padding: '18px 22px', maxHeight: '68vh', overflowY: 'auto' }}>
          {(!fromReq && products.length === 0) ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>{t.none}</p>
          ) : (
            <>
              {fromReq ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{pname}</div>
                  <p style={{ fontSize: 11, color: excl ? '#9A7B4F' : 'var(--text-tertiary)', margin: '0 0 12px' }} data-invoice-note={excl ? `exclusive-${cur.toLowerCase()}` : 'usd'}>{excl ? (cur === 'USD' ? t.exNoteUsd : t.exNote) : t.priceNote}</p>
                  {excl && curPicker}
                  {rows.map((r, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <input value={r.label} onChange={(e) => setRow(i, 'label', e.target.value)} style={{ ...input, flex: 2, marginTop: 0 }} placeholder={t.variant} />
                      <input type="number" value={r.qty} onChange={(e) => setRow(i, 'qty', e.target.value)} style={{ ...input, flex: 1, marginTop: 0 }} placeholder={t.qty} />
                      <input type="number" value={r.unit_price} onChange={(e) => setRow(i, 'unit_price', e.target.value)} style={{ ...input, flex: 1, marginTop: 0 }} placeholder={t.unit} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    <label style={{ ...lbl, flex: 1 }}>{t.material}<input value={material} onChange={(e) => setMaterial(e.target.value)} style={input} /></label>
                    <label style={{ ...lbl, flex: 1 }}>{t.lead}<input value={lead} onChange={(e) => setLead(e.target.value)} style={input} /></label>
                  </div>
                  {excl && <label style={{ ...lbl, display: 'block', marginTop: 10 }}>{t.exShip(curLbl)}<input type="number" min="0" value={ship} onChange={(e) => setShip(e.target.value)} style={input} data-invoice-shipping="1" /></label>}
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    <label style={{ ...lbl, flex: 1 }}>{t.incoterms}<input value={incoterms} onChange={(e) => setIncoterms(e.target.value)} style={input} placeholder="FOB…" /></label>
                    <label style={{ ...lbl, flex: 1 }}>{t.port}<input value={port} onChange={(e) => setPort(e.target.value)} style={input} /></label>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 11, color: excl ? '#9A7B4F' : 'var(--text-tertiary)', margin: '0 0 12px' }} data-invoice-note={excl ? `exclusive-${cur.toLowerCase()}` : 'usd'}>{excl ? (cur === 'USD' ? t.exNoteUsd : t.exNote) : t.priceNote}</p>
                  {excl && curPicker}
                  <label style={{ ...lbl, display: 'block' }}>{t.product}
                    <select value={productRef} onChange={(e) => setProductRef(e.target.value)} style={input}>
                      {products.map((p) => <option key={p.id} value={p.id}>{(isAr ? p.name_ar : p.name_en) || p.name_ar || p.name_en || p.id}</option>)}
                    </select>
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <label style={{ ...lbl, flex: 1 }}>{t.qty}<input type="number" value={qty} onChange={(e) => setQty(e.target.value)} style={input} /></label>
                    <label style={{ ...lbl, flex: 1 }}>{excl ? t.exPrice(curLbl) : t.price}<input type="number" value={price} onChange={(e) => setPrice(e.target.value)} style={input} data-invoice-price="1" /></label>
                  </div>
                  {excl && <label style={{ ...lbl, display: 'block', marginTop: 12 }}>{t.exShip(curLbl)}<input type="number" min="0" value={ship} onChange={(e) => setShip(e.target.value)} style={input} data-invoice-shipping="1" /></label>}
                  <label style={{ ...lbl, display: 'block', marginTop: 12 }}>{t.notes}<input value={notes} onChange={(e) => setNotes(e.target.value)} style={input} /></label>
                </>
              )}
              <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--bg-subtle)', borderRadius: 10 }}>
                {excl ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-secondary)' }}><span>{t.subtotal}</span><span>{money(subtotal, cur)}</span></div>
                    {shipping > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}><span>{t.shipLine}</span><span>{money(shipping, cur)}</span></div>}
                  </>
                ) : <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#9A7B4F', fontWeight: 600 }}><span>{t.fee}</span><span>{money(fee, cur)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginTop: 6 }}><span>{t.total}</span><span data-invoice-total={total}>{money(total, cur)}</span></div>
                {traderSar != null && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#9A7B4F', fontWeight: 600, marginTop: 4 }}><span>{t.traderPays}</span><span data-invoice-trader-sar={traderSar}>≈ {money(traderSar, 'SAR')}</span></div>}
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border-muted)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>{t.cancel}</button>
          {(fromReq || products.length > 0) && (
            <button onClick={doIssue} disabled={busy || !productRef} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#141210', color: '#F7F4EE', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{busy ? t.issuing : t.issue}</button>
          )}
        </div>
      </div>
    </div>
  );
}
